import { useQuery } from '@tanstack/react-query';
import { CALENDAR_EVENT_KIND, parseSkateEvent, isFeaturedActive, type SkateEvent } from '@/lib/types';
import { useSkateRelays } from '@/hooks/useSkateRelays';
import { useAdminModeration, filterAdminHidden } from '@/hooks/useAdminModeration';

export function useSkateEvents(limit = 50) {
  const { querySkateContent, getQueryKeySuffix } = useSkateRelays();
  const hidden = useAdminModeration();

  return useQuery({
    queryKey: ['skate-events', limit, getQueryKeySuffix()],
    select: (events: SkateEvent[]) => filterAdminHidden(events, CALENDAR_EVENT_KIND, hidden),
    queryFn: async () => {
      const signal = AbortSignal.timeout(15000);

      const events = await querySkateContent(
        [
          {
            kinds: [CALENDAR_EVENT_KIND],
            '#t': ['skateboarding'],
            limit,
          },
        ],
        { signal }
      );

      const skateEvents: SkateEvent[] = [];
      for (const event of events) {
        const skateEvent = parseSkateEvent(event);
        if (skateEvent) skateEvents.push(skateEvent);
      }

      // Featured (paid) events first, then by soonest start time.
      return skateEvents.sort((a, b) => {
        const aF = isFeaturedActive(a.featuredUntil) ? 1 : 0;
        const bF = isFeaturedActive(b.featuredUntil) ? 1 : 0;
        if (aF !== bF) return bF - aF;
        return a.start - b.start;
      });
    },
    staleTime: 60000,
  });
}

export function useUpcomingSkateEvents(limit = 20) {
  const { querySkateContent, getQueryKeySuffix } = useSkateRelays();
  const hidden = useAdminModeration();
  const now = Math.floor(Date.now() / 1000);

  return useQuery({
    queryKey: ['upcoming-skate-events', limit, getQueryKeySuffix()],
    select: (events: SkateEvent[]) => filterAdminHidden(events, CALENDAR_EVENT_KIND, hidden),
    queryFn: async () => {
      const signal = AbortSignal.timeout(15000);

      const events = await querySkateContent(
        [
          {
            kinds: [CALENDAR_EVENT_KIND],
            '#t': ['skateboarding'],
            limit: limit * 2, // Fetch more to filter
          },
        ],
        { signal }
      );

      const skateEvents: SkateEvent[] = [];
      for (const event of events) {
        const skateEvent = parseSkateEvent(event);
        // Only include future events
        if (skateEvent && skateEvent.start >= now) {
          skateEvents.push(skateEvent);
        }
      }

      // Featured (paid) events first, then soonest start time.
      return skateEvents
        .sort((a, b) => {
          const aF = isFeaturedActive(a.featuredUntil) ? 1 : 0;
          const bF = isFeaturedActive(b.featuredUntil) ? 1 : 0;
          if (aF !== bF) return bF - aF;
          return a.start - b.start;
        })
        .slice(0, limit);
    },
    staleTime: 60000,
  });
}

export function useSkateEvent(identifier: string, pubkey?: string) {
  const { querySkateContent, getQueryKeySuffix } = useSkateRelays();
  const hidden = useAdminModeration();

  return useQuery({
    queryKey: ['skate-event', identifier, pubkey, getQueryKeySuffix()],
    select: (evt: SkateEvent | null) =>
      evt && hidden.has({ coord: `${CALENDAR_EVENT_KIND}:${evt.pubkey}:${evt.identifier}`, id: evt.id })
        ? null
        : evt,
    queryFn: async () => {
      const signal = AbortSignal.timeout(10000);

      const filter: {
        kinds: number[];
        '#d': string[];
        authors?: string[];
        limit: number;
      } = {
        kinds: [CALENDAR_EVENT_KIND],
        '#d': [identifier],
        limit: 1,
      };

      if (pubkey) {
        filter.authors = [pubkey];
      }

      const events = await querySkateContent([filter], { signal });

      if (events.length === 0) return null;

      return parseSkateEvent(events[0]);
    },
    enabled: !!identifier,
    staleTime: 60000,
  });
}
